import { getAccessToken } from './authTokens';

/** Read claims from the JWT in localStorage. */
function readJwtPayload(): Record<string, unknown> | null {
  try {
    const token = getAccessToken();
    if (!token) return null;
    const encoded = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    if (!encoded) return null;
    return JSON.parse(atob(encoded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function toFiniteNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/** Read numeric user id from the JWT in localStorage. */
export function getCurrentUserIdFromToken(): number | null {
  const payload = readJwtPayload();
  if (!payload) return null;

  const nestedUser =
    payload.user && typeof payload.user === 'object'
      ? (payload.user as Record<string, unknown>)
      : null;

  return (
    toFiniteNumber(payload.userId) ??
    toFiniteNumber(payload.user_id) ??
    toFiniteNumber(payload.id) ??
    toFiniteNumber(nestedUser?.id) ??
    toFiniteNumber(nestedUser?.userId) ??
    // `sub` is often the user id, but may also be an email/uuid — only use numeric values
    toFiniteNumber(payload.sub)
  );
}

/** Read email from the JWT when present (used to identify the current user in connections). */
export function getCurrentUserEmailFromToken(): string | null {
  const payload = readJwtPayload();
  if (!payload) return null;

  const nestedUser =
    payload.user && typeof payload.user === 'object'
      ? (payload.user as Record<string, unknown>)
      : null;

  const candidates = [
    payload.email,
    payload.userEmail,
    payload.username,
    nestedUser?.email,
    // non-numeric `sub` is sometimes the email
    typeof payload.sub === 'string' && !Number.isFinite(Number(payload.sub))
      ? payload.sub
      : null,
  ];

  for (const value of candidates) {
    if (typeof value === 'string' && value.includes('@')) {
      return value.trim().toLowerCase();
    }
  }
  return null;
}
